import { defineStore, acceptHMRUpdate } from 'pinia'
import type { ApiError } from '@/api/client'
import { ShiftPlanService } from '@/api/client'
import { Shift, type TShiftData } from '@/classes/Shift'
import { useAuthStore } from '@/stores/AuthStore'
import { DateTime } from 'luxon'
import handleRequest from '@/helpers/handleRequest'
import { useConfigStore } from './ConfigStore'

type TState = {
  shifts: Shift[]
  nextShift: Shift | null
  lastUpdate: DateTime | null
  lastCalendarView: 'month' | 'week' | 'day'
  seenShifts: { [key: number]: boolean }
}

const initialState: TState = {
  shifts: [],
  nextShift: null,
  lastUpdate: null,
  lastCalendarView: 'week',
  seenShifts: {},
}

const useShiftStore = defineStore('shiftStore', {
  state: () => {
    return { ...initialState }
  },
  getters: {
    noData: (state) => {
      return state.lastUpdate === null
    },
    upcomingShifts: (state) => {
      const now = DateTime.now()
      return state.shifts
        .filter((shift) => shift.startDate && shift.startDate > now)
        .sort((a, b) => {
          if (!a.startDate || !b.startDate) return 0
          return a.startDate < b.startDate ? -1 : 1
        })
    },
    pastShifts: (state) => {
      const now = DateTime.now()
      return state.shifts
        .filter((shift) => shift.endDate && shift.endDate < now)
        .sort((a, b) => {
          if (!a.startDate || !b.startDate) return 0
          return a.startDate < b.startDate ? -1 : 1
        })
    },
    activeShift: (state) => {
      const now = DateTime.now()
      return (
        state.shifts.find(
          (shift) =>
            shift.startDate &&
            shift.endDate &&
            now >= shift.startDate &&
            now <= shift.endDate,
        ) ?? null
      )
    },
    shiftsCount: (state) => {
      return state.shifts.length
    },
    upcomingShift: (state) => {
      if (
        state.nextShift?.startDate &&
        state.nextShift?.startDate < DateTime.now().plus({ days: 7 })
      ) {
        return state.nextShift
      }
      return undefined
    },
    hasUnseenChanges(state) {
      return state.shifts.some((shift) => {
        return (
          (!!shift.data.changedFields!.length &&
            !state.seenShifts[shift.data.id!]) ||
          (shift.data.isNew && !state.seenShifts[shift.data.id!])
        )
      })
    },
  },
  actions: {
    async sync() {
      if (!useConfigStore().shift_plans_enabled) return
      try {
        // Fetch all upcoming shifts
        const allShiftsResponse = await handleRequest(
          ShiftPlanService.getUpcomingShifts(),
        )

        if (allShiftsResponse?.data && Array.isArray(allShiftsResponse.data)) {
          const shifts: Shift[] = []
          allShiftsResponse.data.forEach((shift) => {
            shifts.push(Shift.createFromServerObject(shift))
          })

          this.shifts = shifts
        } else {
          this.shifts = []
        }

        // Fetch next upcoming shift
        const nextShiftResponse = await handleRequest(
          ShiftPlanService.getUpcomingShift(),
        )

        if (
          nextShiftResponse?.data &&
          !Array.isArray(nextShiftResponse.data) &&
          nextShiftResponse.data.id
        ) {
          this.nextShift = Shift.createFromServerObject(nextShiftResponse.data)
        } else {
          this.nextShift = null
        }

        this.lastUpdate = DateTime.now()
      } catch (error) {
        const apiError = error as ApiError
        if (apiError.status === 401) {
          await useAuthStore().logout()
        } else {
          throw error
        }
      }
    },
    async getShiftById(id: number): Promise<Shift | null> {
      if (this.$state.shifts.length === 0) {
        await this.sync()
      }
      const shift = this.$state.shifts.find((shift) => shift.data.id === id)
      return shift || null
    },
    getShiftsByDateRange(start: DateTime, end: DateTime): Shift[] {
      return this.$state.shifts
        .filter((shift) => {
          if (!shift.startDate || !shift.endDate) return false
          return (
            (shift.startDate >= start && shift.startDate <= end) ||
            (shift.endDate >= start && shift.endDate <= end) ||
            (shift.startDate <= start && shift.endDate >= end)
          )
        })
        .sort((a, b) => {
          if (!a.startDate || !b.startDate) return 0
          return a.startDate < b.startDate ? -1 : 1
        })
    },
    getShiftsByPlace(placeId: number): Shift[] {
      return this.$state.shifts
        .filter((shift) => shift.data.placeId === placeId)
        .sort((a, b) => {
          if (!a.startDate || !b.startDate) return 0
          return a.startDate < b.startDate ? -1 : 1
        })
    },
    getShiftsByPlan(planId: number): Shift[] {
      return this.$state.shifts
        .filter((shift) => shift.data.planId === planId)
        .sort((a, b) => {
          if (!a.startDate || !b.startDate) return 0
          return a.startDate < b.startDate ? -1 : 1
        })
    },
    getNextShift(): Shift | null {
      return this.nextShift
    },
    hydrate(state: string) {
      this.$state = JSON.parse(state, (key, value) => {
        if (key === 'shifts') {
          return value.map((shiftData: TShiftData) => {
            return Shift.createFromObject(shiftData)
          })
        } else if (key === 'nextShift') {
          return value ? Shift.createFromObject(value) : null
        } else if (key === 'lastUpdate') {
          return value ? DateTime.fromISO(value) : null
        } else {
          return value
        }
      })
    },
    clearData() {
      this.$state = { ...initialState }
    },

    markShiftAsSeen(shift: Shift) {
      this.seenShifts[shift.data.id!] = true
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useShiftStore, import.meta.hot))
}

export { useShiftStore }
